import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { CircularProgress, LinearProgress } from '@mui/material'
import styled from '@emotion/styled'
import { H3 } from '../../../layouts/TextStyles'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import GroupStudentsTable from './components/GroupStudentsTable'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import Breadcrumb from '../../../components/Breadcrumb'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { useInfiniteScroll } from '../../../app/hooks/useInfiniteScroll'
import { StudentSectionAnalysisState } from '../../../features/types'

const GroupStudentsPage = () => {
    const { formatMessage } = useIntl()
    const { state } = useLocation()
    const { groupId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams('')
    const [monthNumber, setMonthNumber] = useState<number | undefined>(
        Number(searchParams.get('monthNumber')) || undefined,
    )
    const [weekNumber, setWeekNumber] = useState<number | undefined>(
        Number(searchParams.get('weekNumber')) || undefined,
    )
    const [rows, setRows] = useState<StudentSectionAnalysisState[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [loadedPages, setLoadedPages] = useState(0)
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)

    const [fetchStudents, { isFetching }] =
        sectionApiSlice.useLazyGetStudentsSectionAnalysisByGroupQuery()

    const hasMore = rows.length < totalCount

    const loadPage = async (nextPage: number) => {
        if (nextPage > 1 && isLoadingRef.current) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true

        try {
            const response = await fetchStudents({
                groupId: +groupId,
                page: nextPage - 1,
                size: DEFAULT_PAGE_SIZE,
                monthNumber,
                weekNumber,
            }).unwrap()

            if (requestId !== requestIdRef.current) return

            setRows((prev) => (nextPage === 1 ? response.data : [...prev, ...response.data]))
            // Короткая страница означает конец списка: расхождение с totalCount
            // иначе гоняло бы наблюдатель по пустым страницам без остановки
            setTotalCount(response.data.length < DEFAULT_PAGE_SIZE
                ? (nextPage - 1) * DEFAULT_PAGE_SIZE + response.data.length
                : response.totalCount)
            setLoadedPages(nextPage)
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) setTotalCount((nextPage - 1) * DEFAULT_PAGE_SIZE)
        } finally {
            if (requestId === requestIdRef.current) isLoadingRef.current = false
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        hasMore && !isFetching && loadedPages > 0,
    )

    // Проценты считаются по темам выбранного периода, поэтому список
    // после смены месяца перечитывается с первой страницы
    const handlePeriodChange = (month?: number, week?: number) => {
        setMonthNumber(month)
        setWeekNumber(week)
    }

    useEffect(() => {
        const params: Record<string, string> = {}
        if (monthNumber) params.monthNumber = monthNumber.toString()
        if (weekNumber) params.weekNumber = weekNumber.toString()
        // replace, иначе каждый выбор месяца кладёт запись в историю и стрелка
        // "назад" возвращает на эту же страницу, а не к списку групп
        setSearchParams(params, { state, replace: true })

        setRows([])
        setTotalCount(0)
        setLoadedPages(0)
        loadPage(1)
    }, [groupId, monthNumber, weekNumber])

    return (
        <Wrapper>
            <Breadcrumb
                links={[
                    formatMessage({ id: 'groups' }),
                    formatMessage({ id: 'studentsList' }),
                ]}
            />

            <Content>
                <Header>
                    <Title>
                        <H3>
                            {formatMessage({ id: 'students' })}{' '}
                            {state?.group?.title}
                        </H3>
                    </Title>

                    <MonthWeekFilter
                        monthNumber={monthNumber}
                        weekNumber={weekNumber}
                        onChange={handlePeriodChange}
                    />
                </Header>

                {isFetching && loadedPages === 0 ? (
                    <LinearProgress />
                ) : (
                    <>
                        <GroupStudentsTable
                            students={rows}
                            isFetching={isFetching}
                        />

                        <Sentinel ref={sentinelRef}>
                            {isFetching && <CircularProgress size={22} />}
                        </Sentinel>
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default GroupStudentsPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Sentinel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    color: #17141A;
`

const Content = styled.div`
    background-color: #fff;
    border: 1px solid #F0E8EE;
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
`
